import { create } from "zustand";
import { getSearchProviders } from "./Searchproviders";
import { tokenize, toResult } from "./Searchranking";
import { SEARCH_APPS } from "./Searchtypes";
import type { AppStatus, SearchAppId, SearchCtx, SearchHit, SearchResult } from "./Searchtypes";

const RECENT_KEY = "cilpron.search.recent";
const TIMEOUT_MS = 9000;

const loadRecent = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, 8) : [];
  } catch {
    return [];
  }
};
const saveRecent = (list: string[]) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* storage can be blocked; recent searches are only a convenience */
  }
};

const idleStatus = (): Record<string, AppStatus> => {
  const out: Record<string, AppStatus> = {};
  const live = new Set(getSearchProviders().map((p) => p.app));
  SEARCH_APPS.forEach((a) => {
    out[a.id] = { state: live.has(a.id) ? "idle" : "unavailable", count: 0 };
  });
  return out;
};

interface SearchState {
  query: string;
  /** the query the shown results belong to */
  searched: string;
  results: SearchResult[];
  status: Record<string, AppStatus>;
  running: boolean;
  recent: string[];
  setQuery: (q: string) => void;
  run: (q: string, ctx: SearchCtx) => void;
  clear: () => void;
  remember: (q: string) => void;
  forgetRecent: () => void;
}

let runId = 0;
let timer: ReturnType<typeof setTimeout> | undefined;

const withTimeout = <T,>(p: Promise<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("This app took too long to answer.")), TIMEOUT_MS);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });

export const useSearchStore = create<SearchState>((set, get) => ({
  query: "",
  searched: "",
  results: [],
  status: idleStatus(),
  running: false,
  recent: loadRecent(),

  setQuery: (q) => set({ query: q }),

  clear: () => {
    runId++;
    if (timer) clearTimeout(timer);
    set({ results: [], searched: "", running: false, status: idleStatus() });
  },

  remember: (q) => {
    const t = q.trim();
    if (!t) return;
    const next = [t, ...get().recent.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 8);
    saveRecent(next);
    set({ recent: next });
  },

  forgetRecent: () => {
    saveRecent([]);
    set({ recent: [] });
  },

  run: (q, ctx) => {
    const text = q.trim();
    if (timer) clearTimeout(timer);
    if (text.length < 2) {
      get().clear();
      return;
    }
    const my = ++runId;
    const lower = text.toLowerCase();
    const tokens = tokenize(text);
    const providers = getSearchProviders();
    const status = idleStatus();
    providers.forEach((p) => {
      status[p.app] = { state: "loading", count: 0 };
    });
    set({ results: [], searched: text, running: true, status });

    let pending = providers.length;
    const done = () => {
      if (my !== runId) return;
      pending--;
      if (pending <= 0) set({ running: false });
    };

    providers.forEach((p) => {
      withTimeout(p.search(text, ctx)).then(
        (hits: SearchHit[]) => {
          if (my !== runId) return;
          const fresh: SearchResult[] = [];
          for (const h of hits) {
            const r = toResult(h, lower, tokens, ctx.currentApp);
            if (r) fresh.push(r);
          }
          set((s) => {
            const seen = new Set(s.results.map((r) => r.key));
            const merged = s.results.concat(fresh.filter((r) => !seen.has(r.key)));
            const st = { ...s.status };
            st[p.app] = { ...st[p.app], state: "done" };
            (Object.keys(st) as SearchAppId[]).forEach((a) => {
              st[a] = { ...st[a], count: merged.filter((r) => r.app === a).length };
            });
            return { results: merged, status: st };
          });
          done();
        },
        (e: unknown) => {
          if (my !== runId) return;
          set((s) => ({
            status: {
              ...s.status,
              [p.app]: { state: "error", count: 0, error: e instanceof Error ? e.message : "Could not search this app." },
            },
          }));
          done();
        }
      );
    });
  },
}));

/** Runs a search after the user pauses typing. */
export const scheduleSearch = (q: string, ctx: SearchCtx, delayMs = 250): void => {
  if (timer) clearTimeout(timer);
  const text = q.trim();
  if (text.length < 2) {
    useSearchStore.getState().clear();
    return;
  }
  timer = setTimeout(() => useSearchStore.getState().run(text, ctx), delayMs);
};